import { Tooltip } from "@/components/common/Tooltip";
import TableRowsOutlinedIcon from "@mui/icons-material/TableRowsOutlined";
import ViewComfyOutlinedIcon from "@mui/icons-material/ViewComfyOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import DatasetLinkedOutlinedIcon from "@mui/icons-material/DatasetLinkedOutlined";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import React from "react";
import JoinIcon from "@/assets/images/join.svg?react";
import InsertLinkIcon from "@mui/icons-material/InsertLink";
import MailOutlineIcon from "@mui/icons-material/MailOutlined";
import CloudOutlinedIcon from "@mui/icons-material/CloudOutlined";
import NetworkCheckOutlinedIcon from "@mui/icons-material/NetworkCheckOutlined";
import ViewColumnOutlinedIcon from "@mui/icons-material/ViewColumnOutlined";
import GridOnOutlinedIcon from "@mui/icons-material/GridOnOutlined";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";

export enum MetricType {
  NActiveRows,
  NTables,
  NImportedTables,
  ColumnsInView,
  ColumnsInTable,
  VersionDelta,
  NDependentViews,
  ModelRole,
  User,
  NModels,
  Team,
  CreationDate,
  LastOpened,
  TeamRole,
  ScheduledDeletion,
  ExpirationTime,
  NJoinTypes,
  NLinks,
  NInvites,
  Storage,
  Egress,
  NImportingModels,
  DiffAdded,
  DiffDeleted,
  DiffModified,
  SchemaColumnsAdded,
  SchemaColumnsRemoved,
  SchemaColumnsModified,
  Release,
  ParentLocked,
  NDatasets,
}

export type MetricIconAccent = "default" | "accent" | "success" | "warning";

function metric_icon(
  metric_type: MetricType,
  accent: MetricIconAccent = "default",
): React.ReactNode | null {
  switch (metric_type) {
    case MetricType.NActiveRows:
      return <TableRowsOutlinedIcon className="icon-sm text-fg-muted-medium" />;
    case MetricType.NTables:
      return (
        <TableChartOutlinedIcon className="icon-sm text-fg-muted-medium" />
      );
    case MetricType.NImportedTables:
      return (
        <DatasetLinkedOutlinedIcon className="icon-sm text-fg-muted-medium" />
      );
    case MetricType.ColumnsInView:
      return (
        <TableRowsOutlinedIcon
          className="icon-sm text-fg-muted-medium"
          sx={{ rotate: "90deg" }}
        />
      );
    case MetricType.ColumnsInTable:
      return (
        <TableRowsOutlinedIcon
          className="icon-sm text-fg-muted-medium"
          sx={{ rotate: "90deg" }}
        />
      );
    case MetricType.NDependentViews:
      return <ViewComfyOutlinedIcon className="icon-sm text-fg-muted-medium" />;
    case MetricType.ModelRole:
      return <VpnKeyOutlinedIcon className="icon-sm text-fg-muted-medium" />;
    case MetricType.User:
      return <PersonOutlinedIcon className="icon-sm text-fg-muted-medium" />;
    case MetricType.NModels:
      return <DashboardOutlinedIcon className="icon-sm text-fg-muted-medium" />;
    case MetricType.Team:
      return <PeopleOutlinedIcon className="icon-sm text-fg-muted-medium" />;
    case MetricType.CreationDate:
      return <CalendarTodayIcon className="icon-sm text-fg-muted-medium" />;
    case MetricType.LastOpened:
      return (
        <VisibilityOutlinedIcon className="icon-sm text-fg-muted-medium" />
      );
    case MetricType.TeamRole:
      return <GroupsOutlinedIcon className="icon-sm text-fg-muted-medium" />;
    case MetricType.ExpirationTime:
      return <AccessTimeIcon className="icon-sm text-fg-muted-medium" />;
    case MetricType.ScheduledDeletion:
      return <DeleteOutlineIcon className="icon-sm text-fg-muted-medium" />;
    case MetricType.NJoinTypes:
      return (
        <JoinIcon
          className={`icon-sm [&_circle]:stroke-current ${
            accent === "accent" ? "text-accent" : "text-fg-muted-medium"
          }`}
        />
      );
    case MetricType.NLinks:
      return <InsertLinkIcon className="icon-sm text-fg-muted-medium" />;
    case MetricType.NInvites:
      return <MailOutlineIcon className="icon-sm text-fg-muted-medium" />;
    case MetricType.Storage:
      return <CloudOutlinedIcon className="icon-sm text-fg-muted-medium" />;
    case MetricType.Egress:
      return (
        <NetworkCheckOutlinedIcon className="icon-sm text-fg-muted-medium" />
      );
    case MetricType.NImportingModels:
      return <InsertLinkIcon className="icon-sm text-warning" />;
    case MetricType.DiffAdded:
      return <TableRowsOutlinedIcon className="icon-sm text-success/60" />;
    case MetricType.DiffDeleted:
      return <TableRowsOutlinedIcon className="icon-sm text-error/60" />;
    case MetricType.DiffModified:
      return <GridOnOutlinedIcon className="icon-sm text-warning/60" />;
    case MetricType.SchemaColumnsAdded:
      return <ViewColumnOutlinedIcon className="icon-sm text-success/60" />;
    case MetricType.SchemaColumnsRemoved:
      return <ViewColumnOutlinedIcon className="icon-sm text-error/60" />;
    case MetricType.SchemaColumnsModified:
      return <ViewColumnOutlinedIcon className="icon-sm text-warning/60" />;
    case MetricType.Release: {
      const colorClass =
        accent === "warning"
          ? "text-warning"
          : accent === "success"
            ? "text-success/60"
            : "text-accent";
      return <RocketLaunchOutlinedIcon className={`icon-sm ${colorClass}`} />;
    }
    case MetricType.ParentLocked:
      return <LockOutlinedIcon className="icon-sm text-fg-muted-medium" />;
    case MetricType.NDatasets:
      return <LayersOutlinedIcon className="icon-sm text-fg-muted-medium" />;
    default:
      return null;
  }
}

/** Default chip-level tooltip text. Callers can override via the
 *  `tooltip` prop on `ItemMetric`. Returning `undefined` means "no
 *  default" — the prop is the only source and the chip stays
 *  tooltip-less when neither is set. */
function default_tooltip(metric_type: MetricType): string | undefined {
  switch (metric_type) {
    case MetricType.NActiveRows:
      return "No. Active Rows";
    case MetricType.NImportedTables:
      return "No. Imported Tables";
    case MetricType.ColumnsInView:
      return "No. Columns in View (Table)";
    case MetricType.ColumnsInTable:
      return "No. Columns in Table";
    case MetricType.NDependentViews:
      return "No. Dependent Views";
    case MetricType.NModels:
      return "No. Models";
    case MetricType.CreationDate:
      return "Creation Date";
    case MetricType.LastOpened:
      return "Last Opened";
    case MetricType.TeamRole:
      return "Team model role";
    case MetricType.ExpirationTime:
      return "Expiration Time";
    case MetricType.ScheduledDeletion:
      return "Scheduled deletion date";
    case MetricType.NJoinTypes:
      return "No. Join Types";
    case MetricType.NLinks:
      return "No. Links";
    case MetricType.NInvites:
      return "Invites";
    case MetricType.Storage:
      return "Storage Quota";
    case MetricType.Egress:
      return "Egress Usage";
    case MetricType.NImportingModels:
      return "No. Models Importing This Table";
    case MetricType.DiffAdded:
      return "No. Rows Added";
    case MetricType.DiffDeleted:
      return "No. Rows Removed";
    case MetricType.DiffModified:
      return "No. Cells Modified";
    case MetricType.SchemaColumnsAdded:
      return "No. Columns Added";
    case MetricType.SchemaColumnsRemoved:
      return "No. Columns Removed";
    case MetricType.SchemaColumnsModified:
      return "No. Columns Modified";
    case MetricType.Release:
      return "Released version";
    case MetricType.ParentLocked:
      return "External source — can't be imported, transferred, or checkpointed";
    case MetricType.NDatasets:
      return "No. Datasets";
    default:
      return undefined;
  }
}

interface ItemMetricProps {
  metric_type: MetricType;
  value?: number | bigint | string | React.ReactNode;
  tooltip?: string;
  iconAccent?: MetricIconAccent;
  hoverAction?: React.ReactNode;
  onClick?: () => void;
  isActive?: boolean;
}

export const ItemMetric: React.FC<ItemMetricProps> = ({
  metric_type,
  value,
  tooltip,
  iconAccent = "default",
  hoverAction,
  onClick,
  isActive = false,
}) => {
  const title = tooltip ?? default_tooltip(metric_type) ?? "";
  return (
    <div
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-pressed={onClick ? isActive : undefined}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      className={`tab-option tab-option-active border-[0.5px] border-border select-none${hoverAction ? " group" : ""}${onClick ? " cursor-pointer hover:border-accent" : " cursor-default"}${isActive ? " border-accent" : ""}`}
    >
      <Tooltip title={title}>
        <span className="inline-flex items-center gap-2">
          {metric_icon(metric_type, iconAccent)}
          {value !== undefined && (
            <span className="text-fg-regular">
              {value === null
                ? "-"
                : React.isValidElement(value)
                  ? value
                  : String(value)}
            </span>
          )}
        </span>
      </Tooltip>
      {hoverAction && (
        <span className="hidden group-hover:inline-flex items-center">
          {hoverAction}
        </span>
      )}
    </div>
  );
};
